import EmblaCarousel, {
  type EmblaCarouselType,
  type EmblaEventType,
  type EmblaOptionsType,
  type EmblaPluginType,
} from 'embla-carousel'

/**
 * Global interface for creating Embla carousels from external scripts.
 * Used by A/B testing tools to dynamically create carousels.
 */
export type EmblaCarouselGlobalApi = {
  /**
   * Create a new Embla carousel instance.
   * @param container - The root element or selector for the carousel
   * @param options - Embla carousel options
   * @param plugins - Optional array of Embla plugins
   * @returns The Embla carousel instance
   * @example
   * const carousel = window.emblaCarousel.create('#my-carousel', { loop: true })
   */
  create: (
    container: HTMLElement | string,
    options?: EmblaOptionsType,
    plugins?: EmblaPluginType[]
  ) => EmblaCarouselType
}

declare global {
  interface Window {
    emblaCarousel?: EmblaCarouselGlobalApi
  }
}

/**
 * Creates an Embla carousel instance.
 * This is a wrapper around the Embla constructor that handles string selectors.
 */
export const createCarousel = (
  container: HTMLElement | string,
  options?: EmblaOptionsType,
  plugins?: EmblaPluginType[]
): EmblaCarouselType => {
  const element =
    typeof container === 'string'
      ? document.querySelector<HTMLElement>(container)
      : container

  if (!element) {
    throw new Error(
      `EmblaCarousel: Container element not found${typeof container === 'string' ? ` for selector "${container}"` : ''}`
    )
  }

  return EmblaCarousel(element, options, plugins)
}

/**
 * Removes the Embla Carousel API from the window object.
 * Useful for cleanup in tests or when the feature should be disabled.
 */
export const removeEmblaFromWindow = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  delete window.emblaCarousel
}

export const carouselActions = {
  create: createCarousel,
}

export type {
  EmblaCarouselType,
  EmblaEventType,
  EmblaOptionsType,
  EmblaPluginType,
}
